import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Spinner, UserLink } from '@/components';
import { DeviceTestPushPanel } from '@/components/admin/DeviceTestPushPanel';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type DeviceItem = {
  installation_id: string;
  nickname: string | null;
  platform: string;
  first_seen_at: string;
  last_seen_at: string;
  unsubscribed_at: string | null;
  app_version: string | null;
  locale: string | null;
  os_version: string | null;
  device_model: string | null;
  brand: string | null;
  hardware_id: string | null;
  system_name: string | null;
  total_memory_mb: number | null;
  cpu_abis: string | null;
  total_disk_mb: number | null;
  screen: string | null;
  is_tablet: boolean | null;
  is_emulator: boolean | null;
  api_level: number | null;
  account_id: string | null;
};

type LinkedUser = {
  id: string;
  display_name: string | null;
  email: string | null;
};

type Detail = {
  device: DeviceItem;
  account: LinkedUser | null;
  recent_runs: Array<Record<string, unknown>>;
  recent_pushes: Array<Record<string, unknown>>;
  recent_resource_taps: Array<Record<string, unknown>>;
  recent_flashcard_views: Array<Record<string, unknown>>;
};

export const Route = createFileRoute('/admin/devices/$installationId')({
  component: DeviceDetail,
});

function DeviceDetail() {
  const { installationId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'device', installationId],
    queryFn: () =>
      apiJson<Detail>(`/v1/admin/devices/${encodeURIComponent(installationId)}`),
  });

  const patch = useMutation({
    mutationFn: (body: { unsubscribe?: boolean; clear_nickname?: boolean }) =>
      apiJson<DeviceItem>(`/v1/admin/devices/${encodeURIComponent(installationId)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'device', installationId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }
  if (!data) return null;
  const d = data.device;
  const account = data.account;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {d.nickname ? d.nickname : 'Device'}
        </h1>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin/devices' })}>
          Back to list
        </Button>
      </div>
      <Card>
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <dt className="text-gray-500">installation_id</dt>
          <dd className="break-all font-mono text-xs">{d.installation_id}</dd>
          <dt className="text-gray-500">platform</dt>
          <dd>{d.platform}</dd>
          <dt className="text-gray-500">nickname</dt>
          <dd>{d.nickname ?? '—'}</dd>
          <dt className="text-gray-500">Signed-in user</dt>
          <dd>
            {account ? (
              <UserLink
                userId={account.id}
                displayName={account.display_name}
                email={account.email}
              />
            ) : (
              '—'
            )}
          </dd>
          <dt className="text-gray-500">last_seen_at</dt>
          <dd>{formatAdminDateTime(d.last_seen_at)}</dd>
          <dt className="text-gray-500">first_seen_at</dt>
          <dd>{formatAdminDateTime(d.first_seen_at)}</dd>
          <dt className="text-gray-500">app_version</dt>
          <dd>{d.app_version ?? '—'}</dd>
          <dt className="text-gray-500">locale</dt>
          <dd>{d.locale ?? '—'}</dd>
          <dt className="text-gray-500">os_version</dt>
          <dd>{d.os_version ?? '—'}</dd>
          <dt className="text-gray-500">device_model</dt>
          <dd>{d.device_model ?? '—'}</dd>
          <dt className="text-gray-500">brand</dt>
          <dd>{d.brand ?? '—'}</dd>
          <dt className="text-gray-500">hardware_id</dt>
          <dd>{d.hardware_id ?? '—'}</dd>
          <dt className="text-gray-500">system_name</dt>
          <dd>{d.system_name ?? '—'}</dd>
          <dt className="text-gray-500">RAM</dt>
          <dd>{d.total_memory_mb != null ? `${d.total_memory_mb} MB` : '—'}</dd>
          <dt className="text-gray-500">cpu_abis</dt>
          <dd>{d.cpu_abis ?? '—'}</dd>
          <dt className="text-gray-500">disk</dt>
          <dd>{d.total_disk_mb != null ? `${d.total_disk_mb} MB` : '—'}</dd>
          <dt className="text-gray-500">screen</dt>
          <dd>{d.screen ?? '—'}</dd>
          <dt className="text-gray-500">tablet</dt>
          <dd>{d.is_tablet == null ? '—' : d.is_tablet ? 'yes' : 'no'}</dd>
          <dt className="text-gray-500">emulator</dt>
          <dd>{d.is_emulator == null ? '—' : d.is_emulator ? 'yes' : 'no'}</dd>
          <dt className="text-gray-500">api_level</dt>
          <dd>{d.api_level ?? '—'}</dd>
          <dt className="text-gray-500">Notifications off at</dt>
          <dd>{d.unsubscribed_at ? formatAdminDateTime(d.unsubscribed_at) : '—'}</dd>
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="danger"
            onClick={() => {
              if (!confirm('Turn off notifications for this installation?')) return;
              void patch.mutate({ unsubscribe: true });
            }}
            loading={patch.isPending}
            disabled={d.unsubscribed_at != null}
          >
            Turn off notifications
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (!confirm('Clear nickname?')) return;
              void patch.mutate({ clear_nickname: true });
            }}
            loading={patch.isPending}
            disabled={!d.nickname}
          >
            Clear nickname
          </Button>
        </div>
      </Card>
      <Card>
        <h2 className="mb-3 text-lg font-medium">Test push</h2>
        <DeviceTestPushPanel devices={[d]} />
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-medium">Recent game runs (20)</h2>
        <ul className="text-xs text-gray-600">
          {data.recent_runs.length === 0 && <li>None</li>}
          {data.recent_runs.map((r, i) => (
            <li key={i} className="mb-1 font-mono">
              {JSON.stringify(r)}
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-medium">Recent engagement sends (20)</h2>
        <ul className="text-xs text-gray-600">
          {data.recent_pushes.length === 0 && <li>None</li>}
          {data.recent_pushes.map((p, i) => (
            <li key={i} className="mb-1 font-mono">
              {JSON.stringify(p)}
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-medium">Recent resource taps (20)</h2>
        <ul className="text-xs text-gray-600">
          {data.recent_resource_taps.length === 0 && <li>None</li>}
          {data.recent_resource_taps.map((t, i) => (
            <li key={i} className="mb-1 font-mono">
              {JSON.stringify(t)}
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-medium">Recent flashcard views (20)</h2>
        <ul className="text-xs text-gray-600">
          {data.recent_flashcard_views.length === 0 && <li>None</li>}
          {data.recent_flashcard_views.map((v, i) => (
            <li key={i} className="mb-1 font-mono">
              {JSON.stringify(v)}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
